import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./chatbot.component.scss";
import exportimages from "../../../assets/images/exportImages";
import { OverlayPanel } from "primereact/overlaypanel";
import { useDispatch, useSelector } from "react-redux";
import {
  chatbotHistoryThunk,
  resetChatbotHistory,
  setChatbotHistory,
} from "../../../rtk-query/features/chatbot/chatbotSlice";
import store from "../../../app/store";
import loaderImage from "../../../assets/images/Chatbot_animated_loader.gif";
import animated_loader_img from "../../../assets/images/animation_loader.gif";
import DOMPurify from "dompurify";
import { _post } from "../../../api/chatbotApiClient";

export const Chatbot = () => {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.authReducer);
  const orguid = store.getState().orgReducer.selectedFI?.orguid;
  const { chatbotHistory, loading, page } = useSelector(
    (state) => state.chatbotReducer,
  );

  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [botStatus, setBotStatus] = useState("");
  const [showAutopilotLabel, setShowAutopilotLabel] = useState(false);
  // const [specialBotText, setSpecialBotText] = useState("");    // this code is for Follow up question.
  const [isFollowUp, setIsFollowUp] = useState(false);

  const overlayRef = useRef(null);
  const bottomRef = useRef(null);
  const prevScrollTop = useRef(null);
  const isFetchingRef = useRef(false);
  // const specialBoxRef = useRef(null);     // this code is for Follow up question.

  const getFormattedTime = () =>
    new Date().toLocaleString("en-US", {
      weekday: "long",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  //Get chatbot response
  const getChatbotResponse = async (text) => {
    let interval;

    try {
      const userMessage = { message: text?.trim() };

      setIsLoading(true);
      setBotStatus("");

      const statuses = ["Thinking...", "Analysing...", "Calculating..."];
      let index = 0;

      // After 2s stop loader
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);

      interval = setInterval(() => {
        if (index < statuses.length) {
          setBotStatus(statuses[index]);
          index++;
        }
      }, 3000); // change status every 3s

      const botResponse = await _post(`${orguid}/chat`, userMessage);

      clearInterval(interval);
      setBotStatus("");

      if (botResponse?.data?.status === "SUCCESS") {
        return botResponse?.data?.data?.response;
      }

      return "⚠️ Something went wrong. Try again.";
    } catch (e) {
      clearInterval(interval);
      setIsLoading(false);
      setBotStatus("");
      return "⏱️ Network timeout. Please check your connection and try again.";
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;

    const time = getFormattedTime();

    dispatch(
      setChatbotHistory([{ sender: "user", text: message, createdOn: time }]),
    );

    setMessage("");
    resetTextareaHeight();
    // setSpecialBotText("");      // this code is for Follow up question.
    setIsFollowUp(false);

    const botReplyText = await getChatbotResponse(message);

    // --------------- this code is for Follow up question. ------------------
    // let finalBotText = botReplyText;

    // if (typeof botReplyText === "string" && botReplyText.includes("#@")) {
    //   const parser = new DOMParser();
    //   const doc = parser.parseFromString(botReplyText, "text/html");

    //   const specialParagraph = Array.from(doc.querySelectorAll("p")).find((p) =>
    //     p.textContent.trim().startsWith("#@"),
    //   );

    //   if (specialParagraph) {
    //     const cleanText = specialParagraph.textContent
    //       .replace(/^#@/, "")
    //       .trim();
    //     setSpecialBotText(cleanText);
    //     setMessage(cleanText);
    //     setIsFollowUp(true);
    //   }

    //   specialParagraph?.remove();
    //   finalBotText = doc.body.innerHTML; // 👈 use local var
    // }

    const botTime = getFormattedTime();

    dispatch(
      setChatbotHistory([
        // { sender: "bot", text: finalBotText, createdOn: botTime },  // this code is for Follow up question.
        { sender: "bot", text: botReplyText, createdOn: botTime },
      ]),
    );
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;

    // Must be scrollable
    if (el.scrollHeight <= el.clientHeight) return;

    // First event — just initialize
    if (prevScrollTop.current === null) {
      prevScrollTop.current = el.scrollTop;
      return;
    }

    const isScrollingUp = prevScrollTop.current > el.scrollTop;

    if (
      isScrollingUp &&
      el.scrollTop === 0 &&
      !loading &&
      !isFetchingRef.current
    ) {
      isFetchingRef.current = true;

      const prevHeight = el.scrollHeight;

      dispatch(chatbotHistoryThunk({ page: page + 1 })).then(() => {
        requestAnimationFrame(() => {
          const newHeight = el.scrollHeight;
          el.scrollTop = newHeight - prevHeight; // preserve position
          isFetchingRef.current = false;
        });
      });
    }

    prevScrollTop.current = el.scrollTop;
  };

  const autoResize = (textarea) => {
    textarea.style.height = "auto";

    const computed = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computed.lineHeight);
    const paddingTop = parseFloat(computed.paddingTop);
    const paddingBottom = parseFloat(computed.paddingBottom);

    const maxLines = 2;
    const maxHeight = lineHeight * maxLines + paddingTop + paddingBottom;

    const chatbot_container = document.querySelector(".chatbot_container");
    const inputWrapper = textarea.parentElement;

    if (textarea.scrollHeight > maxHeight) {
      textarea.style.height = maxHeight + "px"; // LOCK height
      textarea.style.overflowY = "auto";
    } else {
      textarea.style.height = textarea.scrollHeight + "px";
      textarea.style.overflowY = "hidden";
    }

    // Dynamically adjust chatbot_container height
    const inputHeight = inputWrapper.offsetHeight;
    chatbot_container.style.height = `calc(100% - ${inputHeight + 5.2 * 13}px)`;
  };

  const resetTextareaHeight = () => {
    const textarea = document.querySelector(".chat-input");
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.overflowY = "hidden";

    const chatbot_container = document.querySelector(".chatbot_container");
    const inputWrapper = textarea.parentElement;

    const inputHeight = inputWrapper.offsetHeight;
    chatbot_container.style.height = `calc(100% - ${inputHeight + 5.2 * 13}px)`;
  };

  useEffect(() => {
    const container = document.querySelector(".chatbot_container");
    if (!container) return;

    const scrollToBottom = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const isOverflowing = container.scrollHeight > container.clientHeight;

          if (isOverflowing) {
            bottomRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
            setShowAutopilotLabel(true);
          } else {
            setShowAutopilotLabel(false);
          }
        });
      });
    };

    scrollToBottom();
  }, [chatbotHistory, isOverlayOpen]);

  useEffect(() => {
    const resize = () => {
      const vh = window.innerHeight;
      document.documentElement.style.setProperty(
        "--chat-height",
        `${vh * 0.85}px`,
      );
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // ---------------- this code is for Follow up question.----------------
  // useEffect(() => {
  //   if (specialBotText && specialBoxRef.current) {
  //     const boxHeightPx = specialBoxRef.current.offsetHeight || 0;
  //     const remHeight = boxHeightPx / 13;

  //     document.documentElement.style.setProperty(
  //       "--special-box-height",
  //       `${remHeight + 1}rem`,
  //     );
  //   } else {
  //     document.documentElement.style.setProperty(
  //       "--special-box-height",
  //       "0rem",
  //     );
  //   }
  // }, [specialBotText]);

  return (
    <>
      <button
        type="button"
        className="btn btn-link flip-container"
        onClick={(e) => {
          overlayRef.current.toggle(e);
          dispatch(chatbotHistoryThunk({ page: page }));
        }}
        style={{ display: isOverlayOpen ? "none" : "block" }}
      >
        <div className="front">
          <img
            src={exportimages?.bont_bot_icon_from}
            className="frontImg"
            alt=""
          />
          <img
            src={exportimages?.bont_bot_icon_back}
            className="backImg"
            alt=""
          />
        </div>
      </button>

      <OverlayPanel
        ref={overlayRef}
        onShow={() => setIsOverlayOpen(true)}
        onHide={() => setIsOverlayOpen(false)}
        className="chatbot_wrapper"
      >
        <div className="chatbot_pannel">
          <div className="chatbot_header">
            <div class="d-flex bd-highlight">
              <div class="me-auto bd-highlight">
                <img
                  src={exportimages?.chatbot_main_icon}
                  className="img-fluid"
                />
                {showAutopilotLabel && !loading && (
                  <label className="autopilot-label">Autopilot Assist</label>
                )}
              </div>
              <div class="bd-highlight">
                <button
                  type="button"
                  className="btn btn-link py-0"
                  onClick={() => {
                    overlayRef.current.hide();
                    setIsOverlayOpen(false);
                    dispatch(resetChatbotHistory());
                  }}
                >
                  <img
                    src={exportimages?.chatbot_Close_icon}
                    className="img-fluid"
                  />
                </button>
              </div>
            </div>
          </div>
          <div
            className="chatbot_container"
            onScroll={(e) => {
              handleScroll(e);
            }}
          >
            <>
              <div className="test">
                <label className="header_one">
                  Hello {authReducer?.userData?.firstName}!
                </label>
                <label className="header_two">I am ARIA</label>
                <p className="subTitle">
                  I’m your virtual analyst for strategic banking decisions.
                </p>
              </div>

              {loading ? (
                <div className="animated_loader">
                  <img
                    alt="loader"
                    loading="lazy"
                    src={animated_loader_img}
                    className="loaderImg"
                    style={{ height: "4rem" }}
                  />
                </div>
              ) : (
                <>
                  {chatbotHistory?.map((chat, index) => (
                    <div
                      key={index}
                      className="chatbot-msg d-flex flex-column justify-content-end"
                    >
                      <span
                        className={`text-msg ${chat.sender === "user" ? "user-msg" : "bot-msg"}`}
                        dangerouslySetInnerHTML={{
                          __html:
                            chat.sender === "bot"
                              ? DOMPurify.sanitize(chat.text)
                              : DOMPurify.sanitize(chat.text),
                        }}
                      />

                      <p
                        className="createdOn"
                        style={{
                          textAlign: chat.sender === "user" ? "end" : "start",
                          width: "95%",
                        }}
                      >
                        {chat?.createdOn}
                      </p>
                    </div>
                  ))}
                </>
              )}

              <div ref={bottomRef} />
              {(isLoading || botStatus) && (
                <div className="chatbot_animated_loader">
                  {isLoading && (
                    <img
                      alt="loader"
                      loading="lazy"
                      src={loaderImage}
                      className="img-fluid"
                      style={{ height: "4rem" }}
                    />
                  )}

                  {!isLoading && botStatus && (
                    <div
                      className="bot-status-text text-center"
                      style={{ height: "3rem" }}
                    >
                      {botStatus}
                    </div>
                  )}
                </div>
              )}
            </>
          </div>
          {/********** // this code is for Follow up question. **************/}
          {/*{specialBotText && (
            <>
              <div
                className="special-question-box"
                ref={specialBoxRef}
                onClick={sendMessage}
              >
                <img
                  src={exportimages?.chatbot_question_icon}
                  className="img-fluid"
                />
                {specialBotText}
              </div>
            </>
          )} */}
          <div className="chatbot_footer">
            <div class="input-group gradient-border">
              <textarea
                value={message}
                className="form-control form-control-lg chat-input"
                placeholder="Ask about EWI, risk signals…"
                rows={1}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setIsFollowUp(false);
                  autoResize(e.target);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault(); // prevent new line on Enter
                    if (!isLoading && message.trim() !== "") {
                      sendMessage();
                    }
                  }
                }}
                style={{ overflow: "hidden", resize: "none" }}
              />
              <button
                type="button"
                className="btn btn-link py-0 send-icon"
                onClick={sendMessage}
                disabled={isLoading || message?.trim() === "" || loading}
              >
                <img
                  src={exportimages?.chatbot_send_icon}
                  className="img-fluid"
                />
              </button>
            </div>
          </div>
        </div>
      </OverlayPanel>
    </>
  );
};
